import axios from 'axios';


// Production URLs (can be overridden with environment variables for easier deploys)
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.fudchat.com/api/';

const getStorageBaseUrl = () => {
  // allow overriding via env for build-time control (production should always set this)
  if (process.env.REACT_APP_STORAGE_BASE_URL) {
    return process.env.REACT_APP_STORAGE_BASE_URL;
  }

  // during local development the frontend often runs on localhost and proxies to the
  // API; in that case it's convenient to use the same origin for storage so the
  // dev server can forward requests.  Detect by NODE_ENV or hostname.
  if (process.env.NODE_ENV === 'development' && typeof window !== 'undefined' && window.location) {
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      return `${window.location.origin}/storage`;
    }
  }

  // default to the canonical production storage host
  return 'https://api.fudchat.com/storage';
};

const STORAGE_BASE_URL = getStorageBaseUrl();

export const resolveImageUrl = (path) => {
  if (!path || typeof path !== 'string') {
     return null;
  }

  // Basic validation to avoid dummy text being treated as paths
  if (path.includes(' ') && !path.startsWith('http') && !path.includes('/') && !path.match(/\.(jpg|jpeg|png|gif|webp|svg|mp4|webm|ogg)$/i)) {
    return null;
  }

  const STORAGE_ORIGIN = new URL(STORAGE_BASE_URL).origin;

  if (path.startsWith('http')) {
    // If it's already a full URL, check if we can make it relative to save on CORS preflights
    // only if the storage origin matches current origin
    try {
      const urlObj = new URL(path);
      if (STORAGE_ORIGIN === window.location.origin && urlObj.origin === STORAGE_ORIGIN) {
        const idx = urlObj.pathname.indexOf('/storage/');
        if (idx !== -1) {
          return urlObj.pathname.substring(idx);
        }
      }
    } catch (e) {}
    
    // Force HTTPS only if we are on HTTPS and it's a production URL
    if (window.location.protocol === 'https:' && path.startsWith('http://') && path.includes('fudchat.com')) {
      return path.replace('http:', 'https:');
    }
    return path;
  }

  // Build from configured base
  const baseUrl = STORAGE_BASE_URL.replace(/\/+$/, '');
  let cleanPath = path.replace(/^\/+/, '');

  // Remove common prefixes to avoid double prefixing
  const prefixes = ['storage/public/', 'storage/', 'public/'];
  for (const prefix of prefixes) {
     if (cleanPath.startsWith(prefix)) {
        cleanPath = cleanPath.substring(prefix.length);
        break;
     }
  }

  const finalUrl = `${baseUrl}/${cleanPath}`;
  
  // If storage is on same origin, use relative path to avoid CORS
  if (STORAGE_ORIGIN === window.location.origin) {
    return `/storage/${cleanPath}`;
  }

  return finalUrl;
};

export const extractStoragePath = (urlOrPath) => {
  if (!urlOrPath || typeof urlOrPath !== 'string') return null;
  
  let path = urlOrPath;
  if (urlOrPath.startsWith('http')) {
    try {
      const urlObj = new URL(urlOrPath);
      path = urlObj.pathname;
    } catch (e) {}
  }

  // Extract part after /storage/
  const storageIdx = path.indexOf('/storage/');
  if (storageIdx !== -1) {
    path = path.substring(storageIdx + 9);
  }

  path = path.replace(/^\/+/, '');
  const prefixes = ['public/'];
  for (const prefix of prefixes) {
     if (path.startsWith(prefix)) {
        path = path.substring(prefix.length);
        break;
     }
  }
  
  return path;
};

export const formatDate = (dateString, options = {}) => {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString);
    const defaultOptions = {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      ...options
    };
    
    return date.toLocaleDateString('en-US', defaultOptions);
  } catch (error) {
    console.error('Error formatting date:', error);
    return dateString;
  }
};

export const formatDateTime = (dateString, options = {}) => {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString);
    const defaultOptions = {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      ...options
    };
    
    return date.toLocaleDateString('en-US', defaultOptions);
  } catch (error) {
    console.error('Error formatting datetime:', error);
    return dateString;
  }
};

const api = axios.create({
  baseURL: API_BASE_URL,
  // Auth is token based (Bearer token from localStorage), so we never need the
  // browser to send/receive cookies cross-site. Keeping this `false` prevents
  // the "Cookie ... rejected because it is in a cross-site context" console
  // warnings (the API lives on api.fudchat.com while the app runs on
  // fudchat.com) and avoids unnecessary CORS preflights.
  withCredentials: false,
  // A request that never resolves pins a connection and leaves the UI spinning
  // forever. Under load, hung requests are what turn a slow API into a dead app,
  // so every call gets a hard ceiling.
  timeout: 20000,
  headers: {
    'Accept': 'application/json',
  },
});

/**
 * Retry idempotent requests on transient failures only.
 *
 * Never retry a mutation: a 5xx/timeout on POST may have already been applied
 * server-side, and replaying it would duplicate content. GETs are safe, and
 * 5xx/429/network errors are the ones where a retry genuinely helps.
 */
const RETRYABLE_METHODS = ['get', 'head', 'options'];
const RETRYABLE_STATUS = [408, 425, 429, 500, 502, 503, 504];
const MAX_RETRIES = 2;

// A single response interceptor handles BOTH concerns. Registering two separate
// `api.interceptors.response.use(...)` error handlers does not chain them — the
// second call *replaces* the first, so the retry/backoff logic was silently dead
// code. Keeping them together guarantees the retry path runs *and* that a 401
// clears the session.
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config || {};
    const method = (config.method || 'get').toLowerCase();
    const status = error.response?.status;

    // --- Session handling (was a second interceptor) -------------------------
    if (status === 401) {
      try {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminUser');
      } catch (e) {
        console.warn('Failed to clear localStorage on 401');
      }
      clearCache();
      if (window.location.pathname.startsWith('/admin') && window.location.pathname !== '/admin/login') {
        window.location.href = '/admin/login';
      }
      // A 401 is not transient: never retry it and do not continue to the
      // retry branch below.
      return Promise.reject(error);
    }

    // --- Transient-failure retry ---------------------------------------------
    const isRetryable =
      RETRYABLE_METHODS.includes(method) &&
      (!error.response || RETRYABLE_STATUS.includes(status)) &&
      // Aborted requests are deliberate (unmount / navigation) — never replay.
      error.code !== 'ERR_CANCELED' &&
      !config.skipRetry;

    if (isRetryable) {
      config.__retryCount = config.__retryCount || 0;
      if (config.__retryCount < MAX_RETRIES) {
        config.__retryCount += 1;
        // Exponential backoff with jitter so a fleet of clients (or one client
        // spamming a failing endpoint) does not retry in lockstep. Honours
        // Retry-After when the server sends one.
        const retryAfter = Number(error.response?.headers?.['retry-after']);
        const backoff = Number.isFinite(retryAfter) && retryAfter > 0
          ? retryAfter * 1000
          : Math.min(1000 * 2 ** (config.__retryCount - 1), 8000) * (0.5 + Math.random() * 0.5);

        await new Promise((resolve) => setTimeout(resolve, backoff));
        return api(config);
      }
    }

    return Promise.reject(error);
  }
);

const cache = new Map();
const pendingRequests = new Map();
const CACHE_TTL = 5 * 60 * 1000;
let cacheGeneration = 0;

// The shape of an empty, type-correct response. Returning this (instead of the
// stale cache of a *different* endpoint — or letting one failing endpoint blank a
// whole page via Promise.all) guarantees a page never silently renders another
// resource type's data. This was the root cause of SCEDL / Postgraduate /
// Research / Amenities "showing the same data".
const EMPTY_RESPONSE = Object.freeze({ data: [] });

/**
 * Fetch a collection endpoint with caching, request de-duplication, and strict
 * per-endpoint error isolation.
 *
 * @param {string} url            Cache key / request path (e.g. 'postgraduates').
 * @param {object} [options]
 * @param {boolean} [options.allowStale=false] Return this endpoint's own stale
 *   cache on failure. Never falls back to another endpoint's data.
 */
const getCached = async (url, { allowStale = false } = {}) => {
  const cached = cache.get(url);
  if (cached && (Date.now() - cached.timestamp < CACHE_TTL)) {
    return cached.data;
  }

  // Deduplicate in-flight requests for the SAME url.
  if (pendingRequests.has(url)) {
    return pendingRequests.get(url);
  }

  const currentGeneration = cacheGeneration;

  // NOTE: the async executor below starts running synchronously, but it only
  // reads the `request` binding from its `finally` block *after* the awaited
  // `api.get()` has resolved or rejected. By then the binding is always
  // initialised, so the self-comparison is safe (it would only be in the
  // temporal dead zone before the first await).
  const request = (async () => {
    try {
      const response = await api.get(url);
      // Only cache if the generation hasn't changed since we started the request
      if (currentGeneration === cacheGeneration) {
        cache.set(url, { data: response, timestamp: Date.now() });
      }
      return response;
    } catch (error) {
      // ONLY reuse this endpoint's own stale cache when explicitly requested.
      // Default is to surface the error so callers can degrade gracefully
      // instead of rendering unrelated data.
      if (allowStale && cached) {
        console.warn(`Request failed for ${url}, returning stale cache`);
        // Keep serving the stale copy, but expire it shortly so the next mount
        // retries instead of pinning the old data for the full 5 minute TTL.
        cache.set(url, { data: cached.data, timestamp: Date.now() - CACHE_TTL + 20000 });
        return cached.data;
      }
      // Never keep a failed entry in the cache.
      cache.delete(url);
      throw error;
    } finally {
      // Clean up the promise from pending only if it's the same one we started
      if (pendingRequests.get(url) === request) {
        pendingRequests.delete(url);
      }
    }
  })();

  pendingRequests.set(url, request);
  return request;
};

/**
 * Fetch a collection endpoint and never throw: on failure resolve to an empty,
 * type-correct `{ data: [] }` response instead of leaking another type's cache.
 * Use this in multi-endpoint Promise.all blocks so a single unavailable endpoint
 * cannot blank an entire page (e.g. the campus map or the admin dashboard).
 */
const getCachedSafe = async (url) => {
  try {
    return await getCached(url, { allowStale: true });
  } catch (error) {
    // `getCached` already retires a cache entry that just failed (and keeps a
    // stale one only for a short retry window), so the empty placeholder below
    // can never be mistaken for a real result on the next mount.
    console.error(`[api] ${url} unavailable:`, error?.message || error);
    // Expose the degradation instead of returning a bare { data: [] } that is
    // indistinguishable from a genuinely empty collection. Admin screens that
    // count these responses need to tell "nothing published" apart from
    // "endpoint down".
    return { ...EMPTY_RESPONSE, unavailable: true };
  }
};

const clearCache = () => {
  cache.clear();
  pendingRequests.clear();
  cacheGeneration++;
};

export { clearCache as clearApiCache };

api.interceptors.request.use(
  (config) => {
    const method = config.method?.toLowerCase();
    if (['post', 'put', 'delete', 'patch'].includes(method)) {
      clearCache();
    }
    try {
      const token = localStorage.getItem('adminToken');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (e) {
      console.warn('Failed to retrieve auth token from localStorage');
    }
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    // Uploads (multipart with images/video) need more headroom than JSON reads.
    // The axios instance already has a default timeout, so checking for a
    // missing timeout here would leave uploads capped at the shorter default.
    if (config.data instanceof FormData) {
      config.timeout = 120000;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// NOTE: the 401/session handler previously registered a SECOND response
// interceptor here. Because axios keeps only the most recently registered error
// handler, this silently replaced the retry interceptor above and made the
// retry/backoff logic dead code. Both behaviors now live in the single
// interceptor defined near MAX_RETRIES.

export const authApi = {
  login: (credentials) => api.post('login', credentials),
  logout: () => api.post('logout'),
  getUser: () => api.get('user'),
  updateProfile: (data) => api.post('user/profile', data),
  changePassword: (data) => api.post('user/password', data),
};

export const facultyApi = {
  getAll: () => getCachedSafe('faculties'),
  getById: (id) => getCached(`faculties/${id}`),
  create: (data) => api.post('faculties', data),
  update: (id, data) => {
    if (data instanceof FormData) {
      return api.post(`faculties/${id}`, data);
    }
    return api.put(`faculties/${id}`, data);
  },
  delete: (id) => api.delete(`faculties/${id}`),
};

export const departmentApi = {
  getAll: () => getCachedSafe('departments'),
  getById: (id) => getCached(`departments/${id}`),
  create: (data) => api.post('departments', data),
  update: (id, data) => {
    if (data instanceof FormData) {
      return api.post(`departments/${id}`, data);
    }
    return api.put(`departments/${id}`, data);
  },
  delete: (id) => api.delete(`departments/${id}`),
};

export const facilityApi = {
  getAll: () => getCachedSafe('facilities'),
  getById: (id) => getCached(`facilities/${id}`),
  create: (data) => api.post('facilities', data),
  update: (id, data) => {
    if (data instanceof FormData) {
      return api.post(`facilities/${id}`, data);
    }
    return api.put(`facilities/${id}`, data);
  },
  delete: (id) => api.delete(`facilities/${id}`),
};

export const amenityApi = {
  getAll: () => getCachedSafe('amenities'),
  getById: (id) => getCached(`amenities/${id}`),
  create: (data) => api.post('amenities', data),
  update: (id, data) => {
    if (data instanceof FormData) {
      return api.post(`amenities/${id}`, data);
    }
    return api.put(`amenities/${id}`, data);
  },
  delete: (id) => api.delete(`amenities/${id}`),
};

export const businessCenterApi = {
  getAll: () => getCachedSafe('business-centers'),
  getById: (id) => getCached(`business-centers/${id}`),
  create: (data) => api.post('business-centers', data),
  update: (id, data) => {
    if (data instanceof FormData) {
      return api.post(`business-centers/${id}`, data);
    }
    return api.put(`business-centers/${id}`, data);
  },
  delete: (id) => api.delete(`business-centers/${id}`),
};

export const noticeApi = {
  getAll: () => getCachedSafe('notices'),
  getById: (id) => getCached(`notices/${id}`),
  create: (data) => api.post('notices', data),
  update: (id, data) => {
    if (data instanceof FormData) {
      return api.post(`notices/${id}`, data);
    }
    return api.put(`notices/${id}`, data);
  },
  delete: (id) => api.delete(`notices/${id}`),
};

export const updateApi = {
  getAll: () => getCachedSafe('updates'),
  getById: (id) => getCached(`updates/${id}`),
  create: (data) => api.post('updates', data),
  update: (id, data) => {
    if (data instanceof FormData) {
      return api.post(`updates/${id}`, data);
    }
    return api.put(`updates/${id}`, data);
  },
  delete: (id) => api.delete(`updates/${id}`),
};

export const documentApi = {
  getAll: () => getCachedSafe('documents'),
  getById: (id) => getCached(`documents/${id}`),
  create: (data) => api.post('documents', data),
  update: (id, data) => {
    if (data instanceof FormData) {
      return api.post(`documents/${id}`, data);
    }
    return api.put(`documents/${id}`, data);
  },
  delete: (id) => api.delete(`documents/${id}`),
};

export const adminBlockApi = {
  getAll: () => getCachedSafe('admin-blocks'),
  getById: (id) => getCached(`admin-blocks/${id}`),
  create: (data) => api.post('admin-blocks', data),
  update: (id, data) => {
    if (data instanceof FormData) {
      return api.post(`admin-blocks/${id}`, data);
    }
    return api.put(`admin-blocks/${id}`, data);
  },
  delete: (id) => api.delete(`admin-blocks/${id}`),
};

export const postgraduateApi = {
  getAll: () => getCachedSafe('postgraduates'),
  getById: (id) => getCached(`postgraduates/${id}`),
  create: (data) => api.post('postgraduates', data),
  update: (id, data) => {
    if (data instanceof FormData) {
      return api.post(`postgraduates/${id}`, data);
    }
    return api.put(`postgraduates/${id}`, data);
  },
  delete: (id) => api.delete(`postgraduates/${id}`),
};

export const researchApi = {
  getAll: () => getCachedSafe('research'),
  getById: (id) => getCached(`research/${id}`),
  create: (data) => api.post('research', data),
  update: (id, data) => {
    if (data instanceof FormData) {
      return api.post(`research/${id}`, data);
    }
    return api.put(`research/${id}`, data);
  },
  delete: (id) => api.delete(`research/${id}`),
};

export const scedlApi = {
  getAll: () => getCachedSafe('scedl'),
  getById: (id) => getCached(`scedl/${id}`),
  create: (data) => api.post('scedl', data),
  update: (id, data) => {
    if (data instanceof FormData) {
      return api.post(`scedl/${id}`, data);
    }
    return api.put(`scedl/${id}`, data);
  },
  delete: (id) => api.delete(`scedl/${id}`),
};

// Admin-only staff directory. It has no public listing endpoint on purpose —
// the records exist so the chatbot can answer questions about a person.
export const staffApi = {
  getAll: () => getCachedSafe('staff'),
  getById: (id) => getCached(`staff/${id}`),
  create: (data) => api.post('staff', data),
  update: (id, data) => {
    if (data instanceof FormData) {
      return api.post(`staff/${id}`, data);
    }
    return api.put(`staff/${id}`, data);
  },
  delete: (id) => api.delete(`staff/${id}`),
};

// Postgraduate / SCEDL programmes are owned by their university-level unit.
export const pgProgrammeApi = {
  getAll: (unitType, admin = false) => {
    const endpoint = admin ? 'admin/pg-programmes' : 'pg-programmes';
    const query = unitType ? `?unit_type=${encodeURIComponent(unitType)}` : '';
    return admin ? api.get(`${endpoint}${query}`) : getCached(`${endpoint}${query}`);
  },
  getById: (id) => getCached(`pg-programmes/${id}`),
  create: (data) => api.post('pg-programmes', data),
  update: (id, data) => api.put(`pg-programmes/${id}`, data),
  delete: (id) => api.delete(`pg-programmes/${id}`),
};

export const aiKnowledgeSourceApi = {
  getAll: () => api.get('ai-knowledge-sources'),
  create: (data) => api.post('ai-knowledge-sources', data),
  update: (id, data) => (data instanceof FormData
    ? api.post(`ai-knowledge-sources/${id}`, data)
    : api.put(`ai-knowledge-sources/${id}`, data)),
  delete: (id) => api.delete(`ai-knowledge-sources/${id}`),
  downloadFile: (id) => api.get(`ai-knowledge-sources/${id}/file`, { responseType: 'blob' }),
};

export const chatApi = {
  sendMessage: (message, userName, history = [], options = {}) => api.post('chat', { 
    message, 
    user_name: userName || localStorage.getItem('fudchat_user_name'),
    history: history.slice(-6).map(m => ({ role: m.role, content: m.content }))
  }, options),
  
  streamMessage: async (message, userName, history = [], { onChunk, onDone, onError, signal }) => {
    // Clear cache on mutation (even though it's streaming, it's a POST request)
    clearCache();

    // Guard against a stream that opens but never delivers a byte (a stuck
    // upstream). Without this, an idle connection keeps the UI in "typing"
    // forever and we never fall back to the non-streaming endpoint.
    const connectTimeoutMs = 20000;
    let connectTimer = null;

    try {
      const response = await fetch(`${API_BASE_URL}chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream',
        },
        body: JSON.stringify({
          message,
          user_name: userName || localStorage.getItem('fudchat_user_name'),
          history: history.slice(-6).map(m => ({ role: m.role, content: m.content })),
          stream: true
        }),
        signal
      });

      if (!response.ok) {
        // Enhanced error handling
        let errorMessage = `Server error (${response.status})`;

        try {
          const errorData = await response.json();
          errorMessage = errorData.content || errorData.message || errorMessage;
        } catch (e) {
          // Response is not JSON, use status text
          errorMessage = response.statusText || `HTTP error! status: ${response.status}`;
        }

        // Provide specific messages for common errors
        if (response.status === 500) {
          errorMessage = 'Server encountered an error. Our team has been notified. Please try again in a moment.';
        } else if (response.status === 503) {
          errorMessage = 'Service temporarily unavailable. Please try again shortly.';
        } else if (response.status === 429) {
          errorMessage = 'Too many requests. Please wait a moment before trying again.';
        }

        throw new Error(errorMessage);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let receivedAnyChunk = false;

      // If the connection is accepted but silent for too long, abort and let the
      // caller fall back to the non-streaming endpoint.
      const armConnectTimeout = () => {
        clearTimeout(connectTimer);
        connectTimer = setTimeout(() => {
          try { reader.cancel(); } catch (e) { /* already closed */ }
        }, connectTimeoutMs);
      };

      armConnectTimeout();

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        if (!receivedAnyChunk) {
          // Bytes are flowing again — stop policing the connection.
          receivedAnyChunk = true;
          clearTimeout(connectTimer);
        }

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop(); // Keep incomplete line in buffer

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.substring(6));
              if (data.done) {
                onDone(data);
              } else if (data.content) {
                onChunk(data.content);
              }
            } catch (e) {
              console.error('Error parsing stream chunk', e);
            }
          }
        }
      }

      // Handle any final data in buffer
      if (buffer && buffer.startsWith('data: ')) {
        try {
          const data = JSON.parse(buffer.substring(6));
          if (data.done) {
            onDone(data);
          } else if (data.content) {
            onChunk(data.content);
          }
        } catch (e) {
          console.error('Error parsing final stream chunk', e);
        }
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        onError(error);
      }
    } finally {
      clearTimeout(connectTimer);
    }
  },

  getSettings: () => getCached('chat/settings'),
  updateSettings: (data) => api.post('chat/settings', data),
  sync: () => api.post('chat/sync'),
  getAnalytics: () => api.get('chat/analytics'),
  getLogs: (page = 1, search = '') => {
    const params = new URLSearchParams({ page: String(page) });
    if (search.trim()) params.set('search', search.trim());
    return api.get(`chat/logs?${params.toString()}`);
  },
};

export const contactApi = {
  submit: (data) => api.post('contact', data),
  getMessages: () => api.get('contacts'),
  // Server-side paginated + searchable listing for the admin table.
  getPaginated: ({ page = 1, perPage = 12, search = '' } = {}) => {
    const params = new URLSearchParams({ paginate: '1', page: String(page), per_page: String(perPage) });
    if (search) params.set('search', search);
    return api.get(`contacts?${params.toString()}`);
  },
  markAsRead: (id) => api.post(`contacts/${id}/read`),
  deleteMessage: (id) => api.delete(`contacts/${id}`),
};

export default api;
